import { GET_ALL_BANNER_QUERY } from '../src/client/cms/queries/banner-queries.gql';
import { GET_TOC_ANNOUNCEMENTS, GET_TOC_WHY_AHM } from '../src/client/models/table-of-contents/queries.gql';
import { GrowthBookPageWrapper, GrowthBookPageWrapperProps, injectGrowthBookProps } from '../utils/growth-book';
import { SLUG } from '../src/client/models/table-of-contents/constants';
import { STATIC_BUILD_REVALIDATE_TIME } from '../src/client/ahm-constants/ui';
import filterHomePageBanners from './utils/filter-home-page-banners';
import getAnnouncementsQuery from './utils/get-announcements-query';
import getCmsData from '../src/client/cms/utils/get-cms-data';
import getOffersContent from './utils/get-offers-content';
import getShouldSkipClientSideRequest from '../src/client/utils/get-should-skip-client-side-request';
import getWhyAhmQuery from './utils/get-why-ahm-query';
import HomePageComponent from '../src/client/pages/home';
import useGetHomeBannersQuery from './utils/use-get-home-banners-query';
import useGetOffersContent from './utils/use-get-offers-content';
import useTableOfContents from '../src/client/cms/utils/use-table-of-contents';
import type { CmsBanner, ValuePropositionContent } from '../src/client/pages/home/types';
import type { ContentTilesProps } from '../src/client/pages/home/components/content-tiles';
import type { Offer, Placeholder } from '../src/client/components/offers/types';

type HomeProps = {
  activeOffers: Offer[];
  announcements: ContentTilesProps['items'];
  banners: CmsBanner[];
  placeholders: Placeholder[];
  valuePropositionAhm: ValuePropositionContent;
};

const HomePage = ({
  activeOffers: serverSideOffers,
  announcements: serverSideAnnouncements,
  banners: serverSideBanners,
  placeholders: serverSidePlaceholders,
  valuePropositionAhm: serverSideValuePropositionAhm,
}: HomeProps) => {
  const shouldSkip = getShouldSkipClientSideRequest();
  const { banners, loading: isBannersLoading } = useGetHomeBannersQuery({ shouldSkip });

  const {
    items: announcements,
    error: announcementsError,
    loading: isAnnouncementsLoading,
  } = useTableOfContents({
    query: GET_TOC_ANNOUNCEMENTS,
    skip: shouldSkip,
    slug: 'announcements',
  });

  const {
    items: valuePropositionAhm,
    error: valuePropositionAhmError,
    loading: isValuePropositionAhmLoading,
  } = useTableOfContents({
    query: GET_TOC_WHY_AHM,
    skip: shouldSkip,
    slug: SLUG.WHY_AHM,
    typename: 'ValuePropositionGroup',
  });

  const { activeOffers, placeholders, loading: isOfferContentLoading } = useGetOffersContent({ shouldSkip });

  const content = {
    activeOffers: shouldSkip || isOfferContentLoading ? serverSideOffers : activeOffers,
    announcements:
      shouldSkip || isAnnouncementsLoading || announcementsError
        ? serverSideAnnouncements
        : (announcements as HomeProps['announcements']),
    banners: shouldSkip || isBannersLoading ? serverSideBanners : banners,
    placeholders: shouldSkip || isOfferContentLoading ? serverSidePlaceholders : placeholders,
    valuePropositionAhm:
      shouldSkip || isValuePropositionAhmLoading || valuePropositionAhmError
        ? serverSideValuePropositionAhm
        : valuePropositionAhm,
  };

  return <HomePageComponent content={content} />;
};

export const getStaticProps = async () => {
  const [cmsBannerData, announcements, { activeOffers, placeholders }, valuePropositionAhm] = await Promise.all([
    getCmsData(GET_ALL_BANNER_QUERY),
    getAnnouncementsQuery(),
    getOffersContent(),
    getWhyAhmQuery(),
  ]);

  const cmsHomePageBannerContent = filterHomePageBanners(cmsBannerData.allBanner);

  const payload = await injectGrowthBookProps({
    // The resolved url is unknown during SSG or ISR (getStaticProps),
    // since during the build the baseURL is unknowable (eg sales-dev01.ahm.ninja).
    // Targeting on URL can only be used client-side or server-side (SSR).
    context: { resolvedUrl: null },
    props: {
      activeOffers,
      announcements,
      banners: cmsHomePageBannerContent || null,
      placeholders,
      valuePropositionAhm,
    },
  });

  return {
    ...payload,
    revalidate: STATIC_BUILD_REVALIDATE_TIME,
  };
};

export default function WrappedPage(props: GrowthBookPageWrapperProps & HomeProps) {
  return GrowthBookPageWrapper(props, HomePage);
}
